import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePackages } from '../context/ProjectContext';
import '../Styles/addPackage.css';

export default function AddPackage() {
  const navigate = useNavigate();
  const { addPackage } = usePackages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    duration: '',
    price: '',
    rating: 4.5,
    inclusions: [''],
    exclusions: [''],
    remark: '',
    days: [
      {
        day: 1,
        title: '',
        description: '',
        highlights: ['']
      }
    ]
  });

  // Handle basic info changes
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Inclusions
  const handleInclusionChange = (index, value) => {
    const updatedInclusions = [...formData.inclusions];
    updatedInclusions[index] = value;
    setFormData(prev => ({
      ...prev,
      inclusions: updatedInclusions
    }));
  };

  const addInclusion = () => {
    setFormData(prev => ({
      ...prev,
      inclusions: [...prev.inclusions, '']
    }));
  };

  const removeInclusion = (index) => {
    if (formData.inclusions.length === 1) {
      alert('At least one inclusion is required');
      return;
    }
    const updatedInclusions = formData.inclusions.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      inclusions: updatedInclusions
    }));
  };

  // Handle Exclusions
  const handleExclusionChange = (index, value) => {
    const updatedExclusions = [...formData.exclusions];
    updatedExclusions[index] = value;
    setFormData(prev => ({
      ...prev,
      exclusions: updatedExclusions
    }));
  };

  const addExclusion = () => {
    setFormData(prev => ({
      ...prev,
      exclusions: [...prev.exclusions, '']
    }));
  };

  const removeExclusion = (index) => {
    if (formData.exclusions.length === 1) {
      alert('At least one exclusion is required');
      return;
    }
    const updatedExclusions = formData.exclusions.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      exclusions: updatedExclusions
    }));
  };

  // Handle day changes
  const handleDayChange = (index, field, value) => {
    const updatedDays = [...formData.days];
    updatedDays[index] = {
      ...updatedDays[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      days: updatedDays
    }));
  };

  // Handle highlight changes
  const handleHighlightChange = (dayIndex, highlightIndex, value) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].highlights[highlightIndex] = value;
    setFormData(prev => ({
      ...prev,
      days: updatedDays
    }));
  };

  // Add new highlight to a day
  const addHighlight = (dayIndex) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].highlights.push('');
    setFormData(prev => ({
      ...prev,
      days: updatedDays
    }));
  };

  // Remove highlight
  const removeHighlight = (dayIndex, highlightIndex) => {
    const updatedDays = [...formData.days];
    updatedDays[dayIndex].highlights.splice(highlightIndex, 1);
    setFormData(prev => ({
      ...prev,
      days: updatedDays
    }));
  };

  // Add new day
  const addDay = () => {
    const newDayNumber = formData.days.length + 1;
    setFormData(prev => ({
      ...prev,
      days: [
        ...prev.days,
        {
          day: newDayNumber,
          title: '',
          description: '',
          highlights: ['']
        }
      ]
    }));
  };

  // Remove day
  const removeDay = (index) => {
    if (formData.days.length === 1) {
      alert('Package must have at least 1 day');
      return;
    }
    const updatedDays = formData.days.filter((_, i) => i !== index);
    // Re-number days
    const renumberedDays = updatedDays.map((day, idx) => ({
      ...day,
      day: idx + 1
    }));
    setFormData(prev => ({
      ...prev,
      days: renumberedDays
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.title.trim()) {
      alert('Please enter package title');
      return false;
    }
    if (!formData.duration.trim()) {
      alert('Please enter package duration (e.g., 8 Days • 7 Nights)');
      return false;
    }
    if (!formData.price.trim()) {
      alert('Please enter package price (e.g., From ₹24,999)');
      return false;
    }
    if (formData.rating < 1 || formData.rating > 5) {
      alert('Rating must be between 1 and 5');
      return false;
    }
    
    // Validate at least one non-empty inclusion
    const hasValidInclusion = formData.inclusions.some(inc => inc.trim() !== '');
    if (!hasValidInclusion) {
      alert('Please add at least one inclusion');
      return false;
    }
    
    // Validate at least one non-empty exclusion
    const hasValidExclusion = formData.exclusions.some(exc => exc.trim() !== '');
    if (!hasValidExclusion) {
      alert('Please add at least one exclusion');
      return false;
    }
    
    for (let i = 0; i < formData.days.length; i++) {
      const day = formData.days[i];
      if (!day.title.trim()) {
        alert(`Please enter title for Day ${day.day}`);
        return false;
      }
      if (!day.description.trim()) {
        alert(`Please enter description for Day ${day.day}`);
        return false;
      }
    }
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Filter out empty values
    const cleanedInclusions = formData.inclusions.filter(inc => inc.trim() !== '');
    const cleanedExclusions = formData.exclusions.filter(exc => exc.trim() !== '');
    const cleanedDays = formData.days.map(day => ({
      ...day,
      highlights: day.highlights.filter(h => h.trim() !== '')
    }));
    
    const packageData = {
      title: formData.title,
      duration: formData.duration,
      price: formData.price,
      rating: formData.rating,
      inclusions: cleanedInclusions,
      exclusions: cleanedExclusions,
      remark: formData.remark,
      days: cleanedDays
    };
    
    const result = await addPackage(packageData);
    
    if (result.success) {
      alert('✅ Package added successfully!');
      navigate('/packages');
    } else {
      alert(`❌ Failed to add package: ${result.error}`);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-package-page">
      <div className="add-package-container">
        <div className="form-header">
          <h1>✨ Add New Travel Package</h1>
          <p>Create an unforgettable journey for your customers</p>
        </div>

        <form onSubmit={handleSubmit} className="package-form">
          {/* Basic Information Section */}
          <div className="form-section">
            <h2>📋 Basic Information</h2>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Package Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleBasicInfoChange}
                  placeholder="e.g., Magical Kerala Tour - 8 Days"
                  required
                />
                <small>Example: Trivandrum - Kovalam - Munnar - Cochin</small>
              </div>
              
              <div className="form-group">
                <label>Duration *</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleBasicInfoChange}
                  placeholder="e.g., 8 Days • 7 Nights"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleBasicInfoChange}
                  placeholder="e.g., From ₹24,999"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Rating (1-5)</label>
                <input
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleBasicInfoChange}
                  step="0.1"
                  min="1"
                  max="5"
                />
                <small>Current rating: {formData.rating} ⭐</small>
              </div>
            </div>
          </div>

          {/* Inclusions Section */}
          <div className="form-section">
            <div className="package-section-header">
              <h2>✅ Inclusions</h2>
              <button type="button" onClick={addInclusion} className="add-item-btn">
                + Add Inclusion
              </button>
            </div>
            <p className="section-description">What's included in this package (e.g., Accommodation, Meals, Transport)</p>
            {formData.inclusions.map((inclusion, index) => (
              <div key={index} className="item-input-group">
                <input
                  type="text"
                  value={inclusion}
                  onChange={(e) => handleInclusionChange(index, e.target.value)}
                  placeholder={`Inclusion ${index + 1}`}
                />
                {formData.inclusions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeInclusion(index)}
                    className="remove-item-btn"
                    title="Remove inclusion"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Exclusions Section */}
          <div className="form-section">
            <div className="package-section-header">
              <h2>❌ Exclusions</h2>
              <button type="button" onClick={addExclusion} className="add-item-btn">
                + Add Exclusion
              </button>
            </div>
            <p className="section-description">What's excluded from this package (e.g., Airfare, Personal expenses, Tips)</p>
            {formData.exclusions.map((exclusion, index) => (
              <div key={index} className="item-input-group">
                <input
                  type="text"
                  value={exclusion}
                  onChange={(e) => handleExclusionChange(index, e.target.value)}
                  placeholder={`Exclusion ${index + 1}`}
                />
                {formData.exclusions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExclusion(index)}
                    className="remove-item-btn"
                    title="Remove exclusion"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Remark Note Section */}
          <div className="form-section">
            <h2>📝 Remark Note</h2>
            <div className="form-group">
              <label>Special Notes / Remarks</label>
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleBasicInfoChange}
                rows="4"
                placeholder="Add any special notes, terms, conditions, or important information about this package..."
              />
              <small>e.g., "Festival surcharges may apply", "Minimum 2 persons required", etc.</small>
            </div>
          </div>

          {/* Itinerary Section */}
          <div className="form-section">
            <div className="package-section-header">
              <h2>🗓️ Itinerary Details</h2>
              <button type="button" onClick={addDay} className="add-day-btn">
                + Add Day
              </button>
            </div>
            
            {formData.days.map((day, index) => (
              <div key={index} className="day-form-card">
                <div className="day-card-header">
                  <h3>Day {day.day}</h3>
                  {formData.days.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => removeDay(index)}
                      className="remove-day-btn"
                    >
                      Remove Day
                    </button>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Day Title *</label>
                  <input
                    type="text"
                    value={day.title}
                    onChange={(e) => handleDayChange(index, 'title', e.target.value)}
                    placeholder="e.g., Trivandrum - Kovalam"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={day.description}
                    onChange={(e) => handleDayChange(index, 'description', e.target.value)}
                    rows="4"
                    placeholder="Describe the day's activities, sightseeing spots, and experiences..."
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>✨ Highlights (Attractions & Activities)</label>
                  {day.highlights.map((highlight, hIndex) => (
                    <div key={hIndex} className="highlight-input-group">
                      <input
                        type="text"
                        value={highlight}
                        onChange={(e) => handleHighlightChange(index, hIndex, e.target.value)}
                        placeholder={`Highlight ${hIndex + 1}`}
                      />
                      {day.highlights.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeHighlight(index, hIndex)}
                          className="remove-highlight-btn"
                          title="Remove highlight"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addHighlight(index)}
                    className="add-highlight-btn"
                  >
                    + Add Highlight
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Preview Section */}
          <div className="form-section">
            <h2>👁️ Preview</h2>
            <div className="preview-card">
              <h3>{formData.title || "Package Title"}</h3>
              <div className="preview-meta">
                <span>⏱️ {formData.duration || "Duration"}</span>
                <span>💰 {formData.price || "Price"}</span>
                <span>⭐ {formData.rating} / 5</span>
              </div>
              
              <div className="preview-inclusions">
                <strong>✅ Inclusions:</strong>
                <ul>
                  {formData.inclusions.filter(inc => inc.trim()).map((inc, idx) => (
                    <li key={idx}>{inc}</li>
                  ))}
                  {formData.inclusions.filter(inc => inc.trim()).length === 0 && <li>No inclusions added yet</li>}
                </ul>
              </div>
              
              <div className="preview-exclusions">
                <strong>❌ Exclusions:</strong>
                <ul>
                  {formData.exclusions.filter(exc => exc.trim()).map((exc, idx) => (
                    <li key={idx}>{exc}</li>
                  ))}
                  {formData.exclusions.filter(exc => exc.trim()).length === 0 && <li>No exclusions added yet</li>}
                </ul>
              </div>
              
              {formData.remark && (
                <div className="preview-remark">
                  <strong>📝 Remark:</strong>
                  <p>{formData.remark}</p>
                </div>
              )}
              
              <div className="preview-days">
                <strong>{formData.days.length} Days Itinerary</strong>
                <ul>
                  {formData.days.map((day, idx) => (
                    <li key={idx}>
                      Day {day.day}: {day.title || "Untitled"}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => {
                if (window.confirm('Are you sure? All unsaved data will be lost.')) {
                  navigate('/packages');
                }
              }} 
              className="cancel-btn"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Package...' : '➕ Add Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}